import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { storage } from "../lib/storage";
import { buildFeedbackEntry } from "../lib/feedback";
import { useAuth } from "./AuthContext";

const FeedbackContext = createContext(null);

/**
 * Operational Validation — local feedback store + Feedback Mode toggle.
 *
 * Hidden by default. Activated via:
 *   • Footer "Feedback" link (only shown to admins)
 *   • Keyboard shortcut ⌘+Shift+F  /  Ctrl+Shift+F
 *   • Query param ?feedback=1 (auto-enables and strips the param)
 *
 * Once enabled the state is persisted in localStorage (`tempo.feedbackMode.v1`)
 * so it survives reloads — a teacher can opt-in for the day and forget about it.
 */
export function FeedbackProvider({ children }) {
    const { currentUser, workspaceMode } = useAuth();
    const [items, setItems] = useState(() => storage.getFeedback());
    const [feedbackMode, _setFeedbackMode] = useState(() =>
        storage.isFeedbackModeOn(),
    );

    // Auto-enable via ?feedback=1 and strip it from the URL.
    useEffect(() => {
        if (typeof window === "undefined") return;
        const url = new URL(window.location.href);
        if (url.searchParams.get("feedback") === "1") {
            _setFeedbackMode(true);
            storage.setFeedbackMode(true);
            url.searchParams.delete("feedback");
            window.history.replaceState({}, "", url.toString());
        }
    }, []);

    const setFeedbackMode = useCallback((on) => {
        _setFeedbackMode(!!on);
        storage.setFeedbackMode(!!on);
    }, []);

    const toggleFeedbackMode = useCallback(
        () => setFeedbackMode(!feedbackMode),
        [feedbackMode, setFeedbackMode],
    );

    const addFeedback = useCallback(
        ({ kind, body, title, page }) => {
            const entry = buildFeedbackEntry({
                kind,
                body,
                title,
                page,
                teacher: currentUser,
                workspaceMode,
            });
            // Defer the persistence to the next microtask. storage.write
            // dispatches a synchronous CustomEvent ('tempo:saved'); when
            // that handler (LastSavedBadge) calls setState while we're
            // still in this updater, React warns about "updating a
            // component while rendering a different component". The
            // microtask boundary lets React commit this update first.
            setItems((prev) => {
                const next = [entry, ...prev];
                queueMicrotask(() => storage.saveFeedback(next));
                return next;
            });
            return entry;
        },
        [currentUser, workspaceMode],
    );

    const updateFeedback = useCallback((id, patch) => {
        setItems((prev) => {
            const next = prev.map((f) => (f.id === id ? { ...f, ...patch } : f));
            queueMicrotask(() => storage.saveFeedback(next));
            return next;
        });
    }, []);

    const removeFeedback = useCallback((id) => {
        setItems((prev) => {
            const next = prev.filter((f) => f.id !== id);
            queueMicrotask(() => storage.saveFeedback(next));
            return next;
        });
    }, []);

    const clearAllFeedback = useCallback(() => {
        setItems([]);
        queueMicrotask(() => storage.saveFeedback([]));
    }, []);

    const api = useMemo(
        () => ({
            feedbackMode,
            setFeedbackMode,
            toggleFeedbackMode,
            items,
            pendingCount: items.filter((f) => f.status === "pending").length,
            addFeedback,
            updateFeedback,
            removeFeedback,
            clearAllFeedback,
        }),
        [
            feedbackMode,
            setFeedbackMode,
            toggleFeedbackMode,
            items,
            addFeedback,
            updateFeedback,
            removeFeedback,
            clearAllFeedback,
        ],
    );

    return (
        <FeedbackContext.Provider value={api}>{children}</FeedbackContext.Provider>
    );
}

export function useFeedback() {
    const ctx = useContext(FeedbackContext);
    if (!ctx) {
        throw new Error("useFeedback must be used within FeedbackProvider");
    }
    return ctx;
}
